import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { getShopProductUrl } from '../../helpers/helpers';
import { TARGET_KINDS, readMediaMetadata, payloadFromHero } from '../../helpers/scope_wheel_helpers';
import WheelMediaDrop from './WheelMediaDrop';
import WheelTargetPicker from './WheelTargetPicker';
import WheelTagPicker from './WheelTagPicker';
import './WheelHeroEditor.scss';

const FK_FOR_KIND = {
  product: 'Product_id',
  category: 'Category_id',
  collection: 'Collection_id',
  department: 'Department_id',
  brand: 'AllBrand_id'
};

const WheelHeroEditor = ({ hero, onSave, onCancel, onShelve, onRestore, onRemove, onStateChange }) => {
  const [draft, setDraft] = useState(() => editorDraftFromHero(hero));
  const [savedDraft, setSavedDraft] = useState(() => editorDraftFromHero(hero));
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = !_.isEqual(payloadFromHero(draft), payloadFromHero(savedDraft));

  useEffect(() => {
    onStateChange?.({ dirty: isDirty, uploading: isUploading, saving: isSaving });
  }, [isDirty, isUploading, isSaving, onStateChange]);
  useEffect(() => () => onStateChange?.({ dirty: false, uploading: false, saving: false }), [onStateChange]);

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const setSourceProductUrl = sourceProductUrl => setDraft(prev => ({
    ...prev, sourceProductUrl, LinkedProduct_id: sourceProductUrl === savedDraft.sourceProductUrl ? savedDraft.LinkedProduct_id : null
  }));

  const setKind = targetKind => {
    setDraft(prev => ({
      ...prev,
      targetKind,
      Product_id: null,
      Category_id: null,
      Collection_id: null,
      Department_id: null,
      AllBrand_id: null,
      brand: null,
      targetLabel: targetKind === 'shopmy' ? 'ShopMy' : ''
    }));
  };

  const setTarget = result => {
    const fk = FK_FOR_KIND[draft.targetKind];
    if (!fk) return;
    setDraft(prev => ({ ...prev, [fk]: result.id, targetLabel: result.label }));
  };

  const setMedia = async (field, url, filename) => {
    const isVideo = field === 'videoUrl';
    const metadata = await readMediaMetadata(url, isVideo);
    setDraft(prev => ({ ...prev, term: prev.term.trim() || filename.replace(/\.[^.]+$/, '').slice(0, 80),
      [field]: url, [isVideo ? 'videoMetadata' : 'imageMetadata']: metadata, isLive: false }));
  };

  const save = async () => {
    if (!draft.term.trim() || isSaving) return;
    setIsSaving(true);
    try {
      const saved = await onSave({ ...draft, term: draft.term.trim() });
      if (saved) {
        const next = editorDraftFromHero(saved);
        setDraft(next);
        setSavedDraft(next);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const needsTarget = !!FK_FOR_KIND[draft.targetKind];
  const needsVideoDestination = draft.targetKind === 'shopmy' && draft.videoUrl && !draft.LinkedProduct_id && !draft.AllBrand_id &&
    (!draft.sourceProductUrl || draft.sourceProductUrl === savedDraft.sourceProductUrl);

  return (
    <fieldset className='wheel-hero-editor' disabled={isSaving || isUploading}>
      <label>
        Title
        <input value={draft.term} onChange={event => setField('term', event.target.value)} placeholder='A name for this hero' maxLength={80} />
      </label>
      <label>
        Search query
        <input
          value={draft.searchQuery || ''}
          onChange={event => setField('searchQuery', event.target.value)}
          placeholder='Optional — if it should search something else'
        />
      </label>
      <div>Show this hero in</div>
      <div className='wheel-hero-editor-kinds'>
        {TARGET_KINDS.map(kind => (
          <button
            key={kind.id}
            type='button'
            className={cn('wheel-hero-editor-kind', { active: draft.targetKind === kind.id })}
            onClick={() => setKind(kind.id)}
          >
            {kind.label}
          </button>
        ))}
      </div>

      {needsTarget && (
        <WheelTargetPicker
          key={draft.targetKind}
          kind={draft.targetKind}
          selectedLabel={draft.targetLabel}
          onSelect={setTarget}
          onClear={() => setDraft(prev => ({ ...prev, [FK_FOR_KIND[draft.targetKind]]: null, targetLabel: '' }))}
        />
      )}

      <div className='wheel-hero-editor-media'>
        <WheelMediaDrop
          label='Hero video'
          url={draft.videoUrl}
          isVideo
          onUpload={(url, name) => setMedia('videoUrl', url, name)}
          onBusyChange={setIsUploading}
          onClear={() => setDraft(prev => ({ ...prev, videoUrl: '', videoMetadata: null, isLive: false }))}
        />
        <WheelMediaDrop
          label='Poster'
          url={draft.imageUrl}
          onUpload={(url, name) => setMedia('imageUrl', url, name)}
          onBusyChange={setIsUploading}
          onClear={() => setDraft(prev => ({ ...prev, imageUrl: '', imageMetadata: null }))}
        />
      </div>

      <label>
        ShopMy product link (optional)
        <input type='url' value={draft.sourceProductUrl || ''} onChange={event => setSourceProductUrl(event.target.value)} placeholder='https://shopmy.us/shop/product/123' />
      </label>
      {draft.sourceProductUrl && (
        <div>
          {draft.LinkedProduct_id ? (
            <a href={getShopProductUrl(draft.LinkedProduct_id)} target='_blank' rel='noreferrer'>Open ShopMy product #{draft.LinkedProduct_id}</a>
          ) : draft.sourceProductUrl !== savedDraft.sourceProductUrl ? 'Save to look up this product.' : 'No available ShopMy product is linked.'}
        </div>
      )}

      {draft.targetKind === 'shopmy' && (
        <>
          <WheelTargetPicker
            kind='brand'
            label='Brand fallback'
            selectedLabel={draft.AllBrand_id ? draft.brand?.name || `Brand #${draft.AllBrand_id}` : ''}
            onSelect={result => setDraft(prev => ({ ...prev, AllBrand_id: result.id, brand: { id: result.id, name: result.label } }))}
            onClear={() => setDraft(prev => ({ ...prev, AllBrand_id: null, brand: null }))}
          />
          <div>Opens this brand when a product is not available.</div>
        </>
      )}

      <WheelTagPicker value={draft.tags} onChange={tags => setField('tags', tags)} isDisabled={isSaving || isUploading} />

      <label>
        Notes
        <textarea value={draft.notes || ''} onChange={event => setField('notes', event.target.value)} rows={3} placeholder='Editorial notes' />
      </label>

      {!!draft.id && draft.status === 'accepted' && (
        <label className='wheel-hero-editor-live'>
          <input type='checkbox' disabled={!draft.imageUrl || !draft.tags.length || (!draft.isLive && needsVideoDestination)} checked={!!draft.isLive} onChange={event => setField('isLive', event.target.checked)} />
          Live on the wheel
        </label>
      )}

      {needsVideoDestination && <div>Choose a brand fallback or link a ShopMy product before publishing this video.</div>}

      <div className='wheel-hero-editor-actions'>
        <button type='button' className='wheel-hero-editor-save' onClick={save} disabled={!draft.term.trim() || isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </button>
        <button type='button' className='wheel-hero-editor-cancel' onClick={onCancel}>
          Cancel
        </button>
        {onShelve && (
          <button type='button' className='wheel-hero-editor-quiet' onClick={onShelve}>
            Shelve
          </button>
        )}
        {onRestore && (
          <button type='button' className='wheel-hero-editor-quiet' onClick={onRestore}>
            Restore to library
          </button>
        )}
        {onRemove && (
          <button type='button' className='wheel-hero-editor-quiet' onClick={onRemove}>
            Remove
          </button>
        )}
      </div>
    </fieldset>
  );
};

const editorDraftFromHero = hero => ({
  ...hero,
  tags: hero.tags || []
});

WheelHeroEditor.propTypes = {
  hero: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onShelve: PropTypes.func,
  onRestore: PropTypes.func,
  onStateChange: PropTypes.func,
  onRemove: PropTypes.func
};

export default WheelHeroEditor;
